// Referral Tracking Hook
// Handles ?ref= parameter from URLs and stores affiliate info in localStorage

import { useEffect, useCallback } from 'react';
import apiClient from '../utils/api';

const REFERRAL_STORAGE_KEY = 'zenta_affiliate_ref';
const REFERRAL_EXPIRY_DAYS = 30;

/**
 * Get stored referral code from localStorage
 */
export const getStoredReferralCode = () => {
  try {
    const data = localStorage.getItem(REFERRAL_STORAGE_KEY);
    if (!data) return null;
    
    const { code, expiresAt } = JSON.parse(data);
    
    // Check if expired
    if (new Date(expiresAt) < new Date()) {
      localStorage.removeItem(REFERRAL_STORAGE_KEY);
      return null;
    }
    
    return code;
  } catch (e) {
    console.error('Error reading referral code:', e);
    return null;
  }
};

/**
 * Store referral code in localStorage
 */
const storeReferralCode = (code) => {
  try {
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + REFERRAL_EXPIRY_DAYS);
    
    localStorage.setItem(REFERRAL_STORAGE_KEY, JSON.stringify({
      code,
      storedAt: new Date().toISOString(),
      expiresAt: expiresAt.toISOString()
    }));
    
    console.log(`✅ Referral code stored: ${code} (expires: ${expiresAt.toLocaleDateString('de-DE')})`);
  } catch (e) {
    console.error('Error storing referral code:', e);
  }
};

/**
 * Clear stored referral code (after successful registration)
 */
export const clearStoredReferralCode = () => {
  localStorage.removeItem(REFERRAL_STORAGE_KEY);
};

/**
 * Hook to track referral from URL parameter
 * Usage: useReferralTracking() in App.js or main layout
 */
export const useReferralTracking = () => {
  const trackReferral = useCallback(async () => {
    try {
      // Check URL for ?ref= parameter
      const urlParams = new URLSearchParams(window.location.search);
      const refCode = urlParams.get('ref');
      
      if (!refCode) return;
      
      console.log(`🔗 Referral code detected in URL: ${refCode}`);
      
      // Validate the referral code
      const result = await apiClient.validateReferralCode(refCode);
      
      if (result.valid) {
        // Store the referral code
        storeReferralCode(refCode);
        
        // Track the click
        try {
          await apiClient.trackAffiliateClick(refCode);
          console.log('📊 Click tracked for affiliate');
        } catch (clickError) {
          // Click tracking is not critical, just log
          console.warn('Click tracking failed:', clickError);
        }
        
        // Remove ref from URL to keep it clean (optional)
        // This keeps the URL clean after tracking
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete('ref');
        window.history.replaceState({}, '', newUrl.toString());
        
        return { success: true, affiliateName: result.affiliate_name };
      } else {
        console.warn(`⚠️ Invalid referral code: ${refCode} - ${result.message}`);
        return { success: false, message: result.message };
      }
    } catch (error) {
      console.error('Error tracking referral:', error);
      return { success: false, error: error.message };
    }
  }, []);
  
  useEffect(() => {
    trackReferral();
  }, [trackReferral]);
  
  return {
    getStoredCode: getStoredReferralCode,
    clearCode: clearStoredReferralCode
  };
};

export default useReferralTracking;
